"use client";
import { useCallback, cloneElement } from "react";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { v4 as uuidv4 } from "uuid";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { usePrevNextButtons } from "./CarouselArrowButtons";
import { useDotButton } from "./CarouselDotButton";
import "./styles.css";

const options: EmblaOptionsType = { loop: true };

export default function HomepageHeroCarousel({
  sections,
  pageTitle = "Build new skills. Advance your career.",
  useAutoplay = false,
  autoplayDelay = 8000,
}: {
  sections: React.ReactNode[];
  pageTitle?: string;
  useAutoplay?: boolean;
  autoplayDelay?: number;
}) {
  const plugins = useAutoplay ? [Autoplay({ delay: autoplayDelay })] : [];
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    autoplay.reset();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  const handleSectionFocus = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (autoplay) {
      autoplay.stop();
    }
  }, [emblaApi]);

  const handleSectionBlur = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (autoplay) {
      autoplay.play();
    }
  }, [emblaApi]);

  return (
    <div className="homepage-hero-carousel embla mx-break-out">
      <h1 className="sr-only">{pageTitle}</h1>

      {/* Slides */}
      <div
        // this z-index should be higher than the one @src/components/PartnerCarousel/PartnerCarousel.tsx
        // and lower than elements that go above:
        // the sitewide banner @src/components/Navigation/DesktopNav/index.tsx
        // and the userMenu @src/components/Navigation/UserMenu/index.tsx
        className="embla__viewport relative z-10"
        ref={emblaRef}
        role="region"
        aria-label="Homepage hero carousel"
        aria-roledescription="carousel"
      >
        <div className="embla__container flex">
          {sections.map((section, index) => {
            // Clone the section and add onFocus and onBlur handlers
            const clonedSection = cloneElement(
              section as React.ReactElement<any>,
              {
                onFocus: handleSectionFocus,
                onBlur: handleSectionBlur,
              },
            );

            return (
              <div
                key={index}
                className="embla__slide"
                aria-label={`Slide ${index + 1}`}
                aria-roledescription="slide"
                aria-hidden={index === selectedIndex ? false : true}
                // ensures tab navigation functions correctly
                inert={index === selectedIndex ? false : true}
              >
                {clonedSection}
              </div>
            );
          })}
        </div>

        {/* Controls */}
        <div className="absolute w-full bottom-0 p-12 pointer-events-none">
          <div className="w-full flex justify-center">
            <div className="flex items-center bg-white gap-2 rounded-full pointer-events-auto">
              <Button
                variant="link"
                size="icon"
                onClick={onPrevButtonClick}
                className="text-primary hover:text-brand"
                disabled={prevBtnDisabled}
              >
                <ChevronLeft svgProps={{ width: 40, height: 40 }} />
                <span className="sr-only">Go to previous slide</span>
              </Button>
              {scrollSnaps.map((_, index) => (
                <Button
                  key={uuidv4()}
                  variant="link"
                  size="icon"
                  onClick={() => onDotButtonClick(index)}
                  className={`hover:text-brand ${index === selectedIndex ? "text-brand" : "text-primary"}`}
                >
                  <Circle svgProps={{ width: 14, height: 14 }} />
                  <span className="sr-only">Go to slide {index + 1}</span>
                </Button>
              ))}
              <Button
                variant="link"
                size="icon"
                onClick={onNextButtonClick}
                className="text-primary hover:text-brand"
                disabled={nextBtnDisabled}
              >
                <ChevronRight svgProps={{ width: 40, height: 40 }} />
                <span className="sr-only">Go to next slide</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
